import { DataSourceService } from './data-source.service';
import { DataManager, Employee, PeriodTypeEnum, Parameter, Contribution, ContributionTypesEnum, ContributionSubTypesEnum, WageTypesEnum, Wage, Loan } from '@akiba/models_bimbo';
import { Injectable } from '@angular/core';
import * as moment from 'moment';

@Injectable()
export class LogicService {
  dataManager: DataManager;

  constructor(private dataSource: DataSourceService) {
    this.dataManager = new DataManager(this.dataSource);
    this.dataManager.parameterDataLoader.load();
  }

  isTodayAPayrollDayForEmployee(employee: Employee): boolean {

    if (!this.dataManager.parameterDataLoader.payrolls || !employee.workInformation.employeeNumber) {
      return true;
    }

    const allApplicablePayroll = this.dataManager.parameterDataLoader.payrolls.filter(p => p.payrollAppliesTo(employee));
    if (allApplicablePayroll.length === 0) {
      return true;
    }

    const result = allApplicablePayroll.find(pd => {

      const now = new Date();
      const dates = pd.payrollDates.find(sd => {

        if (sd.active) {
          if (sd.endDate) {
            return sd.endDate >= now && sd.startDate <= now;
          }
        }

        return false;

      });

      if (dates !== undefined) {
        return true;
      }

      return false;

    });

    return result !== undefined;

  }

  private currentSavingParameter(employee: Employee): Parameter {
    const parameters = this.dataManager.parameterDataLoader.parameters ? this.dataManager.parameterDataLoader.parameters : [];
    const currentPermanentSavingPeriod = parameters.filter(parameterObj => parameterObj.type === PeriodTypeEnum.permanentSaving).find(e => e.savingParameterAppliesTo(employee));
    return currentPermanentSavingPeriod;
  }

  maxSavingContribution(employee: Employee): number {

    const currentPermanentSaving = this.currentSavingParameter(employee);
    if (!this.dataManager.parameterDataLoader.payrolls) {
      return 0;
    }

    if (!currentPermanentSaving) {
      return 0;
    }

    const payrollP = this.dataManager.parameterDataLoader.payrolls.find(p => p.payrollAppliesTo(employee));
    if (!payrollP) {
      return 0;
    }

    const now = new Date();
    const dateArray = new Array<Date>();
    let pastDates = 0;

    payrollP.payrollDatesForLoans.forEach(cd => {

      if (cd.active) {
        if (cd.startDate) {
          if (now >= cd.startDate) {
            dateArray.push(cd.startDate);
          }
        }
      }

    });

    let step1 = 0;
    const pt = Number(employee.workInformation.wage.wageID);

    const wage = employee.workInformation && employee.workInformation.wage ? employee.workInformation.wage : new Wage();

    let amount = wage.amount;
    if (employee.retirementSaving.toLowerCase() === 's') {
      amount = wage.averageSalary > 0 ? (wage.averageSalary) : (wage.amount);
    }


    if (!isNaN(pt) && pt === 2) {

      step1 += (amount * 30) * 0.3;
      pastDates = 2;

    } else {

      step1 += (amount * 30) * 0.3;
      pastDates = 4;

    }

    step1 -= Math.abs((currentPermanentSaving.savingParameter.UMA * 30) * 0.3);

    let lastFACFund = new Array<Contribution>();
    let lastFALFund = new Array<Contribution>();

    let lastCAR = new Contribution();
    let lastFund = new Array<Contribution>();
    let pcTotal: Contribution = new Contribution();

    if (employee.funds && employee.funds.length > 0) {

      lastFACFund = employee.funds.filter(element => element.type === ContributionTypesEnum.FAC);
      lastFALFund = employee.funds.filter(element => element.type === ContributionTypesEnum.FAL);

      lastFACFund.sort((a, b) => {
        return b.regDate.getTime() - a.regDate.getTime();
      });

      lastFALFund.sort((a, b) => {
        return b.regDate.getTime() - a.regDate.getTime();
      });

      const temp1 = lastFACFund.slice(0, pastDates);
      const temp2 = lastFALFund.slice(0, pastDates);

      lastFund = temp1.concat(temp2);
      pcTotal = lastFund.reduce((p, n) => {

        p.companyAmount += n.companyAmount;
        p.employeeAmount += n.employeeAmount;

        return p;

      }, new Contribution());

    }

    if (employee.retirementSavingsData) {

      const contributionArray = employee.retirementSavingsData.map(rec => new Contribution(rec)).filter(c => c.subType !== ContributionSubTypesEnum.NotSet && c.subType.toUpperCase().includes('X') === false && c.subType.toString() !== "CAR_DR" && c.subType.toString() !== "CAR_DRE");
      contributionArray.sort((a, b) => {
        return b.regDate.getTime() - a.regDate.getTime();
      });

      const temp1 = contributionArray.slice(0, 2);
      lastCAR = temp1.reduce((p, n) => {

        p.companyAmount += n.companyAmount;
        p.employeeAmount += n.employeeAmount;

        return p;

      }, new Contribution());

    }

    const amountToSubtract = pcTotal.employeeAmount + lastCAR.employeeAmount;

    if (!isNaN(pt) && pt === 2) {

      const r = step1 - amountToSubtract;
      return (r && r > 0 ) ? r / 2 : 0;

    } else {

      const r = step1 - amountToSubtract;
      const t = (r && r > 0 ) ? r / 4 : 0;
      return t;

    }

  }

  minContribution(employee: Employee): number {

    const currentPermanentSavingPeriod = this.currentSavingParameter(employee);
    if (!currentPermanentSavingPeriod) {
      return 0;
    }

    const pt = Number(employee.workInformation.wage.wageID);
    if (!isNaN(pt) && pt === 2) {
      return currentPermanentSavingPeriod.savingParameter.biWeeklyMin;
    }

    return currentPermanentSavingPeriod.savingParameter.weeklyMin;

  }

  maxContribution(employee: Employee): number {

    const pt = Number(employee.workInformation.wage.wageID);
    if (!isNaN(pt) && pt === 2) {
      return 0.30 * employee.workInformation.wage.amount;
    }

    return employee.workInformation.wage.amount;

  }

  shouldShowSavingExtras(employee: Employee): boolean {

    const pt = employee.workInformation.wage.payrollType;
    if (pt !== WageTypesEnum.VE && pt !== WageTypesEnum.PR) {
      return true;
    }
    return false;

  }

  shouldCalculateAVGWage(employee: Employee): boolean {
    if (employee.retirementSaving.toLowerCase() === 's' && employee.workInformation.wage.averageSalary > 0) {
      return true;
    } else {
      return false;
    }
    //  return employee.retirementSaving.toLowerCase() === 's';
  }

  //////////////////////////////////////////////////////////////////////////////////////////////////////////////////

  async activeShareSavings(employee: Employee): Promise<any> {

    const date = new Date();
    const shareSavingsParams: any = {};

    if (employee.workInformation) {

      const companyID = employee.workInformation.companyRef ? employee.workInformation.companyRef.companyID : undefined;
      const contractID = employee.workInformation.contract ? employee.workInformation.contract.contractID : undefined;
      const wageID = employee.workInformation.wage ? employee.workInformation.wage.wageID : undefined;
      const wageAmount: number = employee.workInformation.wage ? employee.workInformation.wage.amount : 1;
      const result = await this.dataSource.loadShareSavingsParameter();

      if (result) {
        const data = result.docs.map(e => e.data());

        if (data) {
          const param = data.find(lt => lt.type === 7);

          if (param) {

            const maxAmount = wageAmount * param.savingParameter.maxDays;
            shareSavingsParams.parametersID = param.parametersID;
            shareSavingsParams.minMonto = param.savingParameter.minAmount;
            shareSavingsParams.downloadURL = param.savingParameter.fileUploaded.downloadURL;
            shareSavingsParams.maxMonto = Number(maxAmount.toFixed(2));
            shareSavingsParams.numSemana = param.savingParameter.firstDiscount.weekNumber;

            const contractNumber = param.savingParameter.contractTypes.find((ct: any) => {
              return ct == contractID;
            });

            const companyNumber = param.savingParameter.includedCompanies.find((ct: any) => {
              return ct.companyID == companyID;
            });

            if (contractNumber && companyNumber) {
              shareSavingsParams.visible = true;
            } else {
              shareSavingsParams.visible = false;
            }


            if (wageID === "2") {
              shareSavingsParams.cuotas = 10;
            } else {
              shareSavingsParams.cuotas = 20;
            }
            // habilitar funcion de compra de acciones fecha 
            param.savingParameter.registrationDates.map((cd: any) => {
              if (cd.active) {

                if (cd.endDate.toDate() >= date && cd.startDate.toDate() <= date) {
                  shareSavingsParams.habilitado = true;
                  shareSavingsParams.scheduleDateID = cd.scheduleDateID;
                } else {
                  shareSavingsParams.habilitado = false;

                }
              } else {
                shareSavingsParams.habilitado = false;
              }

            });

          }

        }
      }
      return shareSavingsParams;
    }

  }



}
